// THE PARTY JOURNAL'S ROW MODEL — copied, not re-derived, from components/PartyStatement.tsx (buildLedgers, buildJournal,
// entryTag, particulars, entryHref), whose functions are file-private. The party page's Overview draws the newest few
// entries of the SAME statement read the Statement tab draws in full (one query key, one read), so it needs the same
// folding: the route's rows are LEGS (one transaction × one asset, with the BFF's own `delta` and `running`), and one
// transaction is ONE row with a line per asset it moved — never a figure netted across unlike assets. Nothing is
// re-summed here. If PartyStatement's model changes, this copy changes with it (or PartyStatement exports it).
import type { StatementRow, MovementState } from '../../../lib/api';
import { ledgerTypeLabel, sameAsset, foldAsset } from '../../../lib/naming';

export interface Leg {
  /** The desk account the leg moved on, as the Books name it (main 141a3bdd): a name, null when no desk account moved
   *  (settle-later, between parties, opening, brought forward), or undefined from a Worker before 141a3bdd. */
  rail_name?: string | null;
  txn_id: string | null;
  at: string | null;
  kind: string;
  words: string;
  reference: string | null;
  deal_id: string | null;
  linked_deal_id: string | null;
  state: MovementState;
  asset: string;
  delta: number;
  running: number;
}

interface Ledger { asset: string; legs: Leg[] }

export interface JournalRow {
  key: string;
  txn_id: string | null;
  at: string | null;
  lead: Leg;
  legs: Leg[];
}

/** The statement's asset order: biggest position first, every asset ever moved after it (PartyStatement assetOptions). */
export function assetOrder(balances: Record<string, number>, box: ReadonlyArray<{ asset: string; balance: number }>, ever: ReadonlyArray<{ asset: string }>): string[] {
  const weight = new Map<string, number>();
  const note = (code: string, v: number) => {
    const a = foldAsset(code);
    weight.set(a, Math.max(weight.get(a) ?? 0, Math.abs(v)));
  };
  for (const [code, v] of Object.entries(balances ?? {})) note(code, v);
  for (const b of box) note(b.asset, b.balance);
  for (const a of ever) if (!weight.has(foldAsset(a.asset))) weight.set(foldAsset(a.asset), 0);
  return [...weight.entries()].sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0])).map(([a]) => a);
}

/** Rows → one ledger per currency (legs of one transaction in one asset fold into one), newest first. */
function buildLedgers(rows: StatementRow[], order: string[]): Ledger[] {
  const assets = [...order];
  for (const r of rows) if (!assets.some((a) => sameAsset(a, r.asset))) assets.push(foldAsset(r.asset));
  const out: Ledger[] = [];
  for (const a of assets) {
    const legs: Leg[] = [];
    for (const r of rows) {
      if (!sameAsset(r.asset, a)) continue;
      const last = legs[legs.length - 1];
      if (last && last.txn_id === r.txn_id) {
        last.delta += r.delta; last.running = r.running;
        if (last.rail_name == null && r.rail_name != null) last.rail_name = r.rail_name;
        continue;
      }
      legs.push({
        txn_id: r.txn_id, at: r.at, kind: r.kind, words: r.words,
        reference: r.deal_id ?? r.reference, deal_id: r.deal_id,
        linked_deal_id: r.linked_deal_id ?? null, state: r.state,
        asset: r.asset, delta: r.delta, running: r.running,
        ...(r.rail_name !== undefined ? { rail_name: r.rail_name } : {}),
      });
    }
    if (legs.length) out.push({ asset: a, legs: legs.reverse() });
  }
  return out;
}

/** The legs regrouped by transaction (`linked_deal_id` alone joins two transactions), newest first. */
export function buildJournal(rows: StatementRow[], order: string[]): JournalRow[] {
  const byKey = new Map<string, JournalRow>();
  for (const L of buildLedgers(rows, order)) {
    for (const e of L.legs) {
      const key = e.linked_deal_id ?? e.txn_id ?? 'before';
      const row = byKey.get(key);
      if (!row) { byKey.set(key, { key, txn_id: e.txn_id, at: e.at, lead: e, legs: [e] }); continue; }
      row.legs.push(e);
      if ((e.at ?? '') > (row.at ?? '')) { row.at = e.at; row.txn_id = e.txn_id; row.lead = e; }
    }
  }
  for (const row of byKey.values()) {
    const distinctTxns = new Set(row.legs.map((l) => l.txn_id)).size;
    if (distinctTxns > 1 && row.lead.kind.toLowerCase() !== 'trade') {
      row.lead = { ...row.lead, kind: 'trade', reference: row.lead.linked_deal_id ?? row.lead.reference };
    }
  }
  return [...byKey.values()].sort((a, b) => {
    if (a.txn_id === null) return 1;
    if (b.txn_id === null) return -1;
    const ta = a.at ?? '', tb = b.at ?? '';
    if (ta !== tb) return ta < tb ? 1 : -1;
    return Number(b.txn_id) - Number(a.txn_id);
  });
}

const MONEY_KINDS = new Set(['transfer', 'party_payment_split', 'receipt', 'payment', 'movement']);

export function isTrade(e: Leg): boolean {
  const k = e.kind.toLowerCase();
  return k === 'trade' || k === 'trade_unsettled';
}

/** The row's tag, in Money movements' words: "Money in / Money out" by the sign, "Between parties", "Trade" … */
export function entryTag(e: Leg): string {
  if (e.txn_id === null) return 'Brought forward';
  const k = e.kind.toLowerCase();
  if (isTrade(e)) return 'Trade';
  if (k === 'p2p') return 'Between parties';
  if (MONEY_KINDS.has(k)) return e.delta === 0 ? 'Money' : e.delta < 0 ? 'Money in' : 'Money out';
  return ledgerTypeLabel(k);
}

/** Where the entry lives: a trade on the Trades log, money on Money movements — each found by its reference. */
export function entryHref(e: Leg): string | null {
  if (!e.reference) return null;
  return isTrade(e) ? `/trades?q=${encodeURIComponent(e.reference)}` : `/movements?q=${encodeURIComponent(e.reference)}`;
}

/** The BFF's sentence cut to a row: the head; the tail after " — " only when the leg moved nothing (it explains the blank). */
export function particulars(words: string): { head: string; tail: string | null } {
  let w = words.replace(/^reversal — /, '').replace(/ \(later reversed\)$/, '');
  let tail: string | null = null;
  const i = w.indexOf(' — ');
  if (i > 0) { tail = w.slice(i + 3); w = w.slice(0, i); }
  return { head: w.charAt(0).toUpperCase() + w.slice(1), tail };
}

/** WHAT A ROW OPENS (Astra, 1 Nov 2026): a row may fold several transactions — a trade and the money linked to its deal.
 *  The trade's deal (its own leg's deal_id, or the deal a money leg is linked to) opens the deal sheet; EACH money
 *  transaction opens its own receipt with its own Reverse door — a linked transfer is a money entry, never its deal.
 *  `tradeTxn`: the trade's own transaction, for its receipt when the deal sheet cannot be drawn. */
export interface EntryDoors {
  deal: string | null;
  tradeTxn: string | null;
  money: Array<{ txn_id: string; words: string; reference: string | null; at: string | null }>;
}
export function entryDoors(r: JournalRow): EntryDoors {
  let deal: string | null = null, tradeTxn: string | null = null;
  const money: EntryDoors['money'] = [];
  for (const l of r.legs) {
    if (l.txn_id === null) continue;
    if (isTrade(l)) {
      deal = deal ?? l.deal_id ?? l.linked_deal_id;
      tradeTxn = tradeTxn ?? l.txn_id;
      continue;
    }
    deal = deal ?? l.linked_deal_id;
    if (!money.some((m) => m.txn_id === l.txn_id)) money.push({ txn_id: l.txn_id, words: l.words, reference: l.reference, at: l.at });
  }
  return { deal, tradeTxn, money };
}
